// Iteration 083 -- Practice Series list + create
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type { EventSeriesPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Plus, Trash2 } from "lucide-react";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function PracticeSeriesPage() {
  const { schoolId } = useParams<{ schoolId: string }>();
  const [series, setSeries] = useState<EventSeriesPublic[]>([]);
  const [canCreate, setCanCreate] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const reload = useCallback(() => {
    if (!schoolId) return;
    setLoading(true);
    api
      .getEventSeriesList(schoolId)
      .then((r) => {
        setSeries(r.series);
        setCanCreate(r._actions.can_create);
      })
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [schoolId]);

  useEffect(() => reload(), [reload]);

  async function remove(id: string) {
    if (!confirm("Delete this series? Future occurrences will be cancelled.")) return;
    await api.deleteEventSeries(id);
    reload();
  }

  if (loading) return <p data-page="practice-series" data-state="loading">Loading…</p>;

  return (
    <div className="flex flex-col gap-4" data-page="practice-series">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">Practice Series</h1>
        {canCreate && !creating && (
          <Button onClick={() => setCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />
            New series
          </Button>
        )}
      </div>
      {error && <p className="text-sm text-destructive" data-error>{error}</p>}

      {creating && schoolId && (
        <CreateSeriesForm
          schoolId={schoolId}
          onCancel={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            reload();
          }}
        />
      )}

      {series.length === 0 ? (
        <p className="text-sm text-muted-foreground">No series yet.</p>
      ) : (
        <div className="space-y-2">
          {series.map((s) => (
            <Card key={s.id}>
              <CardHeader className="flex flex-row items-start justify-between">
                <div>
                  <CardTitle className="text-base">{s.name}</CardTitle>
                  <p className="text-sm text-muted-foreground">
                    {s.days_of_week.map((d) => DAY_NAMES[d]).join(", ")} · {s.default_starts_at_local} ·{" "}
                    {s.default_duration_mins}m
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {s.starts_on} → {s.ends_on} · {s.occurrence_count} upcoming
                  </p>
                </div>
                {s._actions.can_edit && (
                  <Button variant="ghost" size="sm" onClick={() => remove(s.id)}>
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                )}
              </CardHeader>
              <CardContent>
                <ul className="space-y-1 text-sm">
                  {s.slots.map((slot) => (
                    <li key={slot.id}>
                      <span className="font-mono text-xs text-muted-foreground mr-2">
                        {slot.slot_starts_at_local}
                      </span>
                      {slot.label ?? slot.team_display_name ?? slot.age_group_name ?? "--"}
                      {slot.court_name && <span className="ml-2 text-muted-foreground">@ {slot.court_name}</span>}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function CreateSeriesForm(props: { schoolId: string; onCancel: () => void; onSaved: () => void }) {
  const [name, setName] = useState("");
  const [startsOn, setStartsOn] = useState(new Date().toISOString().slice(0, 10));
  const [endsOn, setEndsOn] = useState(
    new Date(Date.now() + 84 * 86400 * 1000).toISOString().slice(0, 10),
  );
  const [days, setDays] = useState<number[]>([1, 3]);
  const [time, setTime] = useState("14:30");
  const [duration, setDuration] = useState(90);
  const [saving, setSaving] = useState(false);

  function toggleDay(d: number) {
    setDays((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d].sort()));
  }

  async function save() {
    setSaving(true);
    try {
      await api.createEventSeries(props.schoolId, {
        name: name.trim() || "Practice",
        event_type: "practice",
        days_of_week: days,
        starts_on: startsOn,
        ends_on: endsOn,
        default_starts_at_local: time,
        default_duration_mins: duration,
        slots: [],
      });
      props.onSaved();
    } catch (e) {
      alert(e instanceof api.ApiError ? e.message : "Failed to save");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">New series</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <input
          className="w-full rounded border px-3 py-2 text-sm"
          placeholder="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <div className="flex gap-2">
          {DAY_NAMES.map((n, d) => (
            <label key={d} className="flex items-center gap-1 text-sm">
              <input type="checkbox" checked={days.includes(d)} onChange={() => toggleDay(d)} />
              {n}
            </label>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <label className="text-sm">
            Starts on
            <input
              type="date"
              className="w-full rounded border px-3 py-2 text-sm"
              value={startsOn}
              onChange={(e) => setStartsOn(e.target.value)}
            />
          </label>
          <label className="text-sm">
            Ends on
            <input
              type="date"
              className="w-full rounded border px-3 py-2 text-sm"
              value={endsOn}
              onChange={(e) => setEndsOn(e.target.value)}
            />
          </label>
          <label className="text-sm">
            Default time
            <input
              type="time"
              className="w-full rounded border px-3 py-2 text-sm"
              value={time}
              onChange={(e) => setTime(e.target.value)}
            />
          </label>
          <label className="text-sm">
            Duration (mins)
            <input
              type="number"
              className="w-full rounded border px-3 py-2 text-sm"
              value={duration}
              onChange={(e) => setDuration(parseInt(e.target.value, 10) || 90)}
            />
          </label>
        </div>
        <div className="flex gap-2">
          <Button onClick={save} disabled={saving}>
            Create
          </Button>
          <Button variant="outline" onClick={props.onCancel}>
            Cancel
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
